// File: /pages/Moves.jsx
import React, { useEffect, useState } from 'react';
import AccordionFolder from '/components/Accordions/AccordionFolder';
import ContentLibrarySidebar from '/components/Layout/ContentLibrarySidebar';
import ContentDisplayTabs from '/components/Tabs/ContentDisplayTabs';
import VideoLinksDisplay from '/components/ContentDisplay/VideoLinksDisplay';
import PerspectiveManagerDisplay from '/components/ContentDisplay/PerspectiveManagerDisplay';
import ClassificationDisplay from '/components/ContentDisplay/ClassificationDisplay';
import AlternativeNamesDisplay from '/components/ContentDisplay/AlternativeNamesDisplay';
import PerspectiveCarousel from '/components/ContentDisplay/PerspectiveCarousel';
import DatabaseImage from '/components/Media/DatabaseImage';
import infoPaneStyles from '/components/Panes/InfoPane.module.css';
import sectionStyles from '/components/Sections/Section.module.css';
import tabsStyles from '/components/Tabs/ContentDisplayTabs.module.css';
import displayStyles from '/components/ContentDisplay/ContentDisplay.module.css';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import styles from './Moves.module.css';

//
// Helper component: renders a ContentDisplay-style accordion for a single move.
//
const MoveContentDisplay = ({ move }) => {
  const { data: session } = useSession();
  const [activeTab, setActiveTab] = useState('Overview');
  const canConfigure = session?.user?.isAdmin === true;
  const contentAdminHref = move.public_hash
    ? `/ContentAdmin?contentHash=${encodeURIComponent(move.public_hash)}`
    : `/ContentAdmin?contentId=${encodeURIComponent(move.content_id)}`;

  // -----------------------------
  // Overview Tab Content
  const overviewTabContent = (
    <div>
      <div className={tabsStyles.overviewContainer}>
        {/* Left Column: Metadata */}
        <div className={displayStyles.leftColumn}>
          <p>
            <strong>Status:</strong> {move.status || 'N/A'}
          </p>
          <p>
            <strong>Submission Date:</strong>{' '}
            {move.submission_date ? new Date(move.submission_date).toLocaleDateString() : 'N/A'}
          </p>
          {move.approval_date && (
            <p>
              <strong>Approval Date:</strong>{' '}
              {new Date(move.approval_date).toLocaleDateString()}
            </p>
          )}
          <p>
            <strong>Created At:</strong>{' '}
            {move.created_at ? new Date(move.created_at).toLocaleDateString() : 'N/A'}
          </p>
          <p>
            <strong>Updated At:</strong>{' '}
            {move.updated_at ? new Date(move.updated_at).toLocaleDateString() : 'N/A'}
          </p>
        </div>
        {/* Right Column: Perspective Carousel and DMCA */}
        <div className={displayStyles.rightColumn}>
          <PerspectiveCarousel content={move} />
          <div className={styles.itemActions}>
            <Link
              href={`/DMCA?contentHash=${encodeURIComponent(move.public_hash || move.content_id)}`}
              className={displayStyles.dmcaLink}
            >
              DMCA Request
            </Link>
            {canConfigure && (
              <Link href={contentAdminHref} className={styles.contentAdminLink}>
                Content Admin <span aria-hidden="true">↗</span>
              </Link>
            )}
          </div>
        </div>
      </div>
      <p className={displayStyles.description}>
        <strong>Description:</strong> {move.description || 'No description available'}
      </p>
      <AlternativeNamesDisplay contentId={move.content_id} />
    </div>
  );

  // -----------------------------
  // Classification Tab Content
  const classificationTabContent = <ClassificationDisplay content={move} />;

  // -----------------------------
  // Media Tab Content
  // Pre-process gallery_images to ensure we have an array.
  const galleryImages = Array.isArray(move.gallery_images) ? move.gallery_images : [];
  const mediaTabContent = (
    <div>
      <h1>Gallery</h1>
      {galleryImages.length > 0 ? (
        <div className={tabsStyles.galleryContainer}>
          {galleryImages.map((image, index) => (
            <DatabaseImage
              key={index}
              image={image}
              alt={`Gallery Image ${index + 1}`}
              className={tabsStyles.galleryImage}
            />
          ))}
        </div>
      ) : (
        <p>No content available yet.</p>
      )}
      <h1>Perspectives</h1>
      <PerspectiveManagerDisplay content={move} />
      <h1>Video Links</h1>
      <VideoLinksDisplay content={move} />
    </div>
  );

  return (
    <ContentDisplayTabs
      tabs={[
        { label: 'Overview', content: overviewTabContent },
        { label: 'Media', content: mediaTabContent },
        { label: 'Classification', content: classificationTabContent },
      ]}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );
};

//
// Main MovesPage: Fetches moves and parent category mapping, groups moves by category,
// and renders them using the AccordionFolder component.
//
const MovesPage = () => {
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parentCategoryMap, setParentCategoryMap] = useState({});

  // Fetch the parent categories mapping from the API.
  useEffect(() => {
    const fetchParentCategories = async () => {
      try {
        const res = await fetch('/api/categories/categories');
        if (!res.ok) throw new Error('Failed to fetch parent categories');
        const data = await res.json();
        if (data.success && data.categories) {
          const map = {};
          data.categories.forEach((cat) => {
            // Use cat.id as key (as a string) mapped to the category name.
            map[String(cat.id)] = cat.category_name;
          });
          setParentCategoryMap(map);
        }
      } catch (err) {
        console.error('Error fetching parent categories mapping:', err);
      }
    };
    fetchParentCategories();
  }, []);

  // Fetch moves (content items with type "Move") from the API.
  useEffect(() => {
    const fetchMoves = async () => {
      try {
        const res = await fetch('/api/content/content?type=Move');
        if (!res.ok) throw new Error('Failed to fetch moves');
        const data = await res.json();
        setMoves(data.content || []);
      } catch (err) {
        console.error('Error fetching moves:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchMoves();
  }, []);

  if (loading) return <p>Loading moves...</p>;
  if (error) return <p>Error: {error}</p>;

  // Group moves by parent category.
  const groups = {};
  moves.forEach((move) => {
    let parentCategories = [];
    if (move.parent_category_id) {
      try {
        // Attempt to parse parent_category_id as JSON.
        parentCategories = JSON.parse(move.parent_category_id);
        if (!Array.isArray(parentCategories)) {
          parentCategories = [parentCategories];
        }
      } catch (err) {
        // Fallback: split a comma-delimited string.
        parentCategories = move.parent_category_id.split(',').map((x) => x.trim());
      }
    }
    // If no category is provided, group under "Uncategorized".
    if (parentCategories.length === 0) {
      if (!groups['Uncategorized']) groups['Uncategorized'] = [];
      groups['Uncategorized'].push(move);
    } else {
      parentCategories.forEach((cat) => {
        // Each category may be an object (with a "value" property) or a primitive.
        const key = String(cat.value || cat);
        if (!groups[key]) groups[key] = [];
        groups[key].push(move);
      });
    }
  });

  // Convert groups object into an array formatted for AccordionFolder.
  const groupsArray = Object.keys(groups).map((groupKey) => {
    let groupName = 'Category ' + groupKey;
    if (groupKey === 'Uncategorized') {
      groupName = 'Uncategorized';
    } else if (parentCategoryMap[groupKey]) {
      groupName = parentCategoryMap[groupKey];
    } else if (parentCategoryMap[parseInt(groupKey)]) {
      groupName = parentCategoryMap[parseInt(groupKey)];
    }
    return {
      groupHeader: groupName,
      items: groups[groupKey].map((move) => ({
        header: move.name,
        content: <MoveContentDisplay move={move} />
      }))
    };
  });

  return (
    <section id="moves" className={`${sectionStyles.section} ${sectionStyles.sectionActive}`}>
      <div className={infoPaneStyles.infoPane}>
        <h1>Shuffle Dance Moves - Our Core Focus:</h1>
        <h2>
          At the heart of ShuffleDB.com lies a meticulously curated collection of shuffle dance moves.
          Click on an accordion header to view the move details.
        </h2>
      </div>
      <div className={sectionStyles.pageContainer}>
        <div className={sectionStyles.mainContent}>
          {groupsArray.length > 0 ? (
            <AccordionFolder groups={groupsArray} />
          ) : (
            <p>No moves found.</p>
          )}
        </div>
        <ContentLibrarySidebar contentType="Move" />
      </div>
    </section>
  );
};

export default MovesPage;
